import React, { useState, useEffect, useRef, useCallback } from "react";
import { Edit, Check, Close } from "@mui/icons-material";
import { Box, Typography, IconButton, Skeleton, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, CircularProgress, Chip, TextField, Button, Tooltip } from "@mui/material";
import styled from "@emotion/styled";
import MarkdownEditor from './MarkdownEditor';
import MarkdownDisplay from "./MarkdownDisplay";
import { useDispatch, useSelector } from 'react-redux';
import { updateProject, setProjectExternalBoard } from '../redux/slices/project';
import PrivacyChip from "./PrivacyChip";
import SkillChip from "./SkillChip";
import DepartmentChip from "./DepartmentChip";
import { getDepartments } from '../redux/ducks/department';
import { getAccountGroups } from '../redux/ducks/account';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsProjectLocked } from '../utils/lockUtils';
import PrivacyDropdown from "./PrivacyDropdown";
import FilePondUploader from "./FilePondUploader";
import { transformAttachments, restoreAttachments } from '../utils/uploadUtils';
import { updateProjectState } from '../redux/slices/project';
import { hasSkill, missingSkillHighlight, selectableSkills, skillOptionLabel } from "../utils/skillUtils";
import AttachmentBox from "./AttachmentBox";
import ConfirmDialog from "./admin/ConfirmDialog";
import { getBugherdContext, getBugherdBoards, syncBugherdBoard, disconnectBugherd } from '../redux/ducks/bugherd';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { showAlert } from '../redux/slices/alerts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../config';    
import { setTagAssignments } from '../redux/ducks/tag';
import TagPicker, { tagSourceNames, useTagDraft } from './TagPicker';

const Title = styled(Typography)(({ theme }) => ({
    display: 'inline-flex',
    fontWeight: theme.typography.fontWeightMedium,
    gap: theme.spacing(1),
    alignItems: 'center',
    flexShrink: 0,
}));

const OverviewBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'missingSkill' })(({ theme, missingSkill }) => ({
    ...(missingSkill ? missingSkillHighlight(theme) : {}),
    [theme.breakpoints.down('sm')]: {
        margin: 0,
        padding: theme.spacing(1, 1),
    }
}))

const TitleContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '16px',
});

const TitleContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
}));

const GroupChip = styled(Chip)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: theme.palette.inverted.main,
    color: theme.palette.inverted.contrastText,
}));

const BoardChip = styled(Chip)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: theme.palette.inverted.main,
    color: theme.palette.inverted.contrastText,
}));

const ChipsGroup = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    minWidth: 0,
});

const EditingContainer = styled(Box)(({ theme }) => ({
    margin: theme.spacing(2, 0, 2),
    display: 'flex',
    gap: theme.spacing(2),
}));

const ExternalBoardLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(0.5),
}));

const BoardActionRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const BoardHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const SyncSummary = styled(Typography, { shouldForwardProp: (prop) => prop !== 'incomplete' })(({ theme, incomplete }) => ({
    color: incomplete ? theme.palette.warning.main : theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const TitleSkeleton = styled(Skeleton)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SubtitleSkeleton = styled(Skeleton)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const BlockSkeleton = styled(Skeleton)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const TagsRow = styled(Box)(({ theme }) => ({
    margin: theme.spacing(2, 0),
}));

const externalBoardLabel = (externalBoard) => {
    const { provider, url } = externalBoard || {};
    if (!provider) return '';
    if (provider === 'bugherd') return 'QA board: BugHerd';
    try {
        return `QA board: ${new URL(url).hostname}`;
    } catch {
        return 'QA board: external';
    }
};

const syncIsIncomplete = (stats) => !!stats && (!!stats.deferred || !!stats.failed || !!stats.timedOut);

const syncSummaryText = (stats) => {
    const parts = [];
    if (stats.created) parts.push(`${stats.created} added`);
    if (stats.updated) parts.push(`${stats.updated} updated`);
    if (stats.recolumned) parts.push(`${stats.recolumned} moved to match BugHerd`);
    if (stats.unchanged) parts.push(`${stats.unchanged} unchanged`);
    if (stats.comments) parts.push(`${stats.comments} comments added`);
    if (stats.archived) parts.push(`${stats.archived} archived on BugHerd, not imported`);
    if (stats.pruned) parts.push(`${stats.pruned} deleted here, archived on BugHerd`);
    if (stats.skippedDeleted) parts.push(`${stats.skippedDeleted} deleted in Pollux, not re-imported`);
    if (stats.missing) parts.push(`${stats.missing} no longer on BugHerd`);
    if (stats.orphansMoved) parts.push(`${stats.orphansMoved} moved off a removed column`);
    if (stats.failed) parts.push(`${stats.failed} failed`);
    if (stats.deferred) parts.push(`${stats.deferred} not synced yet`);
    if (stats.timedOut) parts.push('ran out of time before finishing');
    const detail = parts.length > 0 ? parts.join(' · ') : 'nothing to import';
    const lead = syncIsIncomplete(stats) ? 'Partly synced' : 'Synced';
    const read = stats.timedOut ? `${stats.remote || 0} tasks read so far` : `${stats.remote || 0} tasks on BugHerd`;
    return `${lead} — ${stats.board || 'Board'}: ${read} — ${detail}`;
};

const ProjectOverview = () => {
    const dispatch = useDispatch();
    const skills = useSelector(state => state.skills);
    const [departments, setDepartments] = useState([]);
    const project = useSelector(state => state.project.currentProject);
    const [isEditing, setIsEditing] = useState(false);
    const [editedOverview, setEditedOverview] = useState('');
    const [currentOverview, setCurrentOverview] = useState('');
    const [currentAttachments, setCurrentAttachments] = useState([]);
    const [editedAttachments, setEditedAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [privacy, setPrivacy] = useState('');
    const [skillId, setSkillId] = useState(null);
    const skillName = useSelector(state => state.project.currentProject?.skill?.name);
    const [departmentId, setDepartmentId] = useState(null);
    const departmentName = useSelector(state => state.project.currentProject?.department?.name);
    const [groups, setGroups] = useState([]);
    const [groupId, setGroupId] = useState('');
    const groupName = useSelector(state => state.project.currentProject?.group?.name);
    const [editedPrivacy, setEditedPrivacy] = useState('');
    const [editedCommentsEnabled, setEditedCommentsEnabled] = useState(false);
    const [editedBoardProvider, setEditedBoardProvider] = useState('');
    const [editedBoardUrl, setEditedBoardUrl] = useState('');
    const [editedBugherdProjectId, setEditedBugherdProjectId] = useState('');
    const [bugherdContext, setBugherdContext] = useState(null);
    const [bugherdBoards, setBugherdBoards] = useState([]);
    const [bugherdLoading, setBugherdLoading] = useState(false);
    const [bugherdSyncing, setBugherdSyncing] = useState(false);
    const [bugherdSyncStats, setBugherdSyncStats] = useState(null);
    const [bugherdError, setBugherdError] = useState(false);
    const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
    const bugherdMounted = useRef(true);
    const [tagsSaving, setTagsSaving] = useState(false);
    const holdEditingRef = useRef(false);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const hasPermission = useHasPermission();
    const canAssignProject = hasPermission('assignProject');
    const canToggleCommenting = hasPermission('toggleCommenting');
    const canEditOverview = hasPermission('editProjectOverview');
    const canSetGroup = hasPermission('setProjectGroup');
    const canEditCompleted = hasPermission('editCompletedProjects');
    const canListDepartments = hasPermission('listDepartments');
    const canEditExternalBoard = hasPermission('editProjects');
    const canSyncBoard = hasPermission('updateQABoard');
    const canSeeInternal = hasPermission('canSeeInternal');
    const boardChipLabel = canSeeInternal ? externalBoardLabel(project?.externalBoard) : '';
    const isLocked = useIsProjectLocked(project);
    const highlightMissingSkill = canSeeInternal && hasPermission('editProjects') && !isLocked && !!project && (!project.completedDate || canEditCompleted) && !hasSkill(project);
    const projectTags = project?.tags || [];
    const tagsEditable = isEditing && !project?.completedDate;
    const tagDraft = useTagDraft({ enabled: tagsEditable, targetType: 'Project', targetId: project?._id, tags: projectTags, prefillInherited: !!project?.tagsStale });
    const tagSources = tagSourceNames(project?.account, project?.account?.client, project?.group);
    const groupChanged = (groupId || '') !== (project?.group?._id || project?.group || '');

    useEffect(() => {
        if (project) {
            setEditedOverview(project.overview || '');
            setCurrentOverview(project.overview || '');
            setPrivacy(project.privacy || '');
            setSkillId(project.skill?._id || null);
            setDepartmentId(project.department?._id || null);
            setGroupId(project.group?._id || project.group || '');
            setEditedPrivacy(project.privacy || '');
            setEditedCommentsEnabled(project.commentsEnabled || false);
            setEditedBoardProvider(project.externalBoard?.provider || '');
            setEditedBoardUrl(project.externalBoard?.url || '');
            setEditedBugherdProjectId(project.externalBoard?.bugherdProjectId || '');
            setCurrentAttachments(transformAttachments(project.attachments || []));
            setEditedAttachments(transformAttachments(project.attachments || []));
            setIsLoading(false);
            if (!holdEditingRef.current) setIsEditing(false);
        } else {
            setIsLoading(false);
        }
    }, [project]);

    useEffect(() => {
        if (canListDepartments) {
            dispatch(getDepartments(0, 100, (departments) => setDepartments(departments || [])));
        }
    }, [dispatch, canListDepartments]);

    const projectId = project?._id;

    useEffect(() => {
        bugherdMounted.current = true;
        return () => { bugherdMounted.current = false; };
    }, []);

    useEffect(() => {
        setBugherdSyncStats(null);
    }, [projectId]);

    const loadBugherd = useCallback(() => {
        if (!projectId) return;
        setBugherdLoading(true);
        setBugherdError(false);
        dispatch(getBugherdContext(projectId, (context) => {
            if (!bugherdMounted.current) return;
            setBugherdContext(context);
            if (!context) {
                setBugherdBoards([]);
                setBugherdError(true);
                setBugherdLoading(false);
                return;
            }
            if (!context.connected) {
                setBugherdBoards([]);
                setBugherdLoading(false);
                return;
            }
            dispatch(getBugherdBoards(context.clientId, (data) => {
                if (!bugherdMounted.current) return;
                if (!data) setBugherdError(true);
                setBugherdBoards(data?.boards || []);
                setBugherdLoading(false);
            }));
        }));
    }, [dispatch, projectId]);

    useEffect(() => {
        if (!isEditing || !canEditExternalBoard || editedBoardProvider !== 'bugherd') return;
        loadBugherd();
    }, [isEditing, canEditExternalBoard, editedBoardProvider, loadBugherd]);

    useEffect(() => {
        const connected = searchParams.get('bugherdConnected');
        const failed = searchParams.get('bugherdError');
        if (!connected && !failed) return;
        if (connected) {
            if (!projectId) return;
            dispatch(showAlert({ severity: 'success', message: 'BugHerd connected for this client' }));
            if (canEditExternalBoard) {
                setEditedBoardProvider('bugherd');
                setIsEditing(true);
            }
        } else {
            dispatch(showAlert({ severity: 'error', message: `BugHerd could not be connected: ${failed}` }));
        }
        const next = new URLSearchParams(searchParams);
        next.delete('bugherdConnected');
        next.delete('bugherdError');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, dispatch, projectId, canEditExternalBoard]);

    const accountId = project?.account?._id;
    useEffect(() => {
        if (!accountId) return;
        dispatch(getAccountGroups(accountId, (data) => {
            setGroups((data || []).filter((group) => !group.completedDate && !group.isDeleted));
        }));
    }, [dispatch, accountId]);

    const handleEditToggle = () => {
        holdEditingRef.current = false;
        setTagsSaving(false);
        if (!isEditing) {
            // When entering edit mode, set the current values
            setEditedOverview(currentOverview);
            setEditedPrivacy(privacy);
            setSkillId(project.skill?._id || null);
            setDepartmentId(project.department?._id || null);
            setGroupId(project.group?._id || project.group || '');
            setEditedCommentsEnabled(project.commentsEnabled || false);
            setEditedBoardProvider(project.externalBoard?.provider || '');
            setEditedBoardUrl(project.externalBoard?.url || '');
            setEditedBugherdProjectId(project.externalBoard?.bugherdProjectId || '');
            setEditedAttachments(transformAttachments(project.attachments || []));
        } else {
            // When canceling, revert to the original values
            setEditedOverview(currentOverview);
            setEditedPrivacy(privacy);
            setSkillId(project.skill?._id || null);
            setDepartmentId(project.department?._id || null);
            setGroupId(project.group?._id || project.group || '');
            setEditedBoardProvider(project.externalBoard?.provider || '');
            setEditedBoardUrl(project.externalBoard?.url || '');
            setEditedBugherdProjectId(project.externalBoard?.bugherdProjectId || '');
            setEditedAttachments(transformAttachments(project.attachments || []));
        }
        setIsEditing(!isEditing);
    };

    const handleOverviewChange = (content) => {
        setEditedOverview(content);
    };

    // A 302 to BugHerd's consent screen, so it has to be a top-level navigation rather than XHR.
    const handleConnectBugherd = () => {
        if (!bugherdContext?.clientId) return;
        const returnTo = encodeURIComponent(location.pathname);
        window.location.href = `${getApiUrl()}/bugherd/${bugherdContext.clientId}/oauthStart?returnTo=${returnTo}`;
    };

    const handleDisconnectBugherd = () => {
        setConfirmingDisconnect(false);
        if (!bugherdContext?.clientId) return;
        dispatch(disconnectBugherd(bugherdContext.clientId, () => loadBugherd()));
    };

    const handleBoardSelect = (value) => {
        setEditedBugherdProjectId(value);
        const board = bugherdBoards.find((b) => String(b.id) === String(value));
        setEditedBoardUrl(board?.url || '');
    };

    const handleSyncBugherd = () => {
        setBugherdSyncing(true);
        dispatch(syncBugherdBoard(project._id, (stats) => {
            setBugherdSyncing(false);
            setBugherdSyncStats(stats || null);
            if (stats) dispatch(triggerRefresh({ triggerName: 'project' }));
        }));
    };

    const externalBoardPayload = () => {
        if (!editedBoardProvider) return null;
        return {
            provider: editedBoardProvider,
            url: editedBoardUrl.trim(),
            ...(editedBoardProvider === 'bugherd' ? { bugherdProjectId: Number(editedBugherdProjectId) } : {}),
        };
    };

    const externalBoardChanged = (next) => {
        const current = project.externalBoard;
        return (current?.provider || '') !== (next?.provider || '')
            || (current?.url || '') !== (next?.url || '')
            || String(current?.bugherdProjectId || '') !== String(next?.bugherdProjectId || '');
    };

    const projectFieldsChanged = () => (
        editedOverview !== (project.overview || '')
        || editedPrivacy !== (project.privacy || '')
        || (skillId || null) !== (project.skill?._id || null)
        || (departmentId || null) !== (project.department?._id || null)
        || (groupId || '') !== (project.group?._id || project.group || '')
        || editedCommentsEnabled !== !!project.commentsEnabled
        || editedAttachments.length !== currentAttachments.length
        || editedAttachments.some((attachment) => !attachment.source || !currentAttachments.some((current) => current.source === attachment.source))
    );

    const saveProjectFields = (onSaved, onFailed) => {
        dispatch(updateProject({
            projectId: project._id, projectData: {
                overview: editedOverview,
                privacy: editedPrivacy,
                skill: skillId,
                department: departmentId,
                group: groupId || null,
                commentsEnabled: editedCommentsEnabled,
                attachments: (restoreAttachments(editedAttachments, currentAttachments)),
            },
            callback: (data) => {
                if (data) {
                    onSaved(data);
                    return;
                }
                setIsLoading(false);
                onFailed();
            },
        }));
        setIsLoading(true);
    };

    const handleSave = () => {
        if (isLocked || tagsSaving) return;

        const tagAssignments = tagsEditable && !groupChanged && tagDraft.dirty ? tagDraft.assignments : null;
        const stopTagSave = () => {
            holdEditingRef.current = false;
            setTagsSaving(false);
        };
        if (tagAssignments) {
            holdEditingRef.current = true;
            setTagsSaving(true);
        }
        const afterFieldsSaved = () => {
            if (tagAssignments) {
                dispatch(setTagAssignments('Project', project._id, tagAssignments, (data) => {
                    stopTagSave();
                    if (!data) return;
                    setIsEditing(false);
                    dispatch(triggerRefresh({ triggerName: 'project' }));
                }));
            } else if (groupChanged) {
                dispatch(triggerRefresh({ triggerName: 'project' }));
            }
        };

        const externalBoard = externalBoardPayload();
        if (canEditExternalBoard && externalBoardChanged(externalBoard)) {
            const fieldsChanged = projectFieldsChanged();
            dispatch(setProjectExternalBoard({
                projectId: project._id,
                externalBoard,
                callback: (data) => {
                    if (!data) {
                        stopTagSave();
                        return;
                    }
                    if (fieldsChanged) {
                        saveProjectFields(afterFieldsSaved, stopTagSave);
                    } else {
                        afterFieldsSaved();
                    }
                },
            }));
            return;
        }

        if (projectFieldsChanged()) saveProjectFields(afterFieldsSaved, stopTagSave);
        else if (tagAssignments) afterFieldsSaved();
        else setIsEditing(false);
    };

    const bugherdPickerReady = editedBoardProvider === 'bugherd' && !bugherdLoading && !bugherdError && !!bugherdContext?.connected;
    const selectedBugherdBoard = bugherdBoards.find((board) => String(board.id) === String(editedBugherdProjectId));
    const boardUrlFromPicker = bugherdPickerReady && !!selectedBugherdBoard?.url;
    const showBugherdIdField = editedBoardProvider === 'bugherd' && !bugherdLoading && !bugherdPickerReady;
    const clientName = project?.account?.client?.name;

    if (isLoading) {
        return (
            <Box>
                <TitleSkeleton variant="text" width="50%" height={40} />
                <SubtitleSkeleton variant="text" width="70%" height={30} />
                <BlockSkeleton variant="rectangular" width="100%" height={150} />
                <Typography variant="h6" component="div">
                    <Skeleton width="40%" />
                </Typography>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="100%" />
            </Box>
        );
    }

    return (
        <OverviewBox missingSkill={highlightMissingSkill}>
            <TitleContainer>
                <TitleContent>
                    <Title variant="h5">Project Overview</Title>
                    <ChipsGroup>
                        {isEditing ? (
                            <PrivacyDropdown value={editedPrivacy} onChange={(e) => setEditedPrivacy(e.target.value)} />
                        ) : (
                            <>
                                {privacy !== "" && privacy !== "Public" && <PrivacyChip privacy={privacy} />}
                                <SkillChip skill={skillName} highlight={highlightMissingSkill} />
                                <DepartmentChip department={departmentName} />
                                <GroupChip label={`Group: ${groupName || "None"}`} size="small" />
                                {boardChipLabel && <BoardChip label={boardChipLabel} size="small" />}
                                {canSyncBoard && project?.externalBoard?.provider === 'bugherd' && project?.externalBoard?.bugherdProjectId && (
                                    <Tooltip title="Pull the latest cards, columns and comments from BugHerd">
                                        <span>
                                            <Button size="small" onClick={handleSyncBugherd} disabled={bugherdSyncing}>
                                                {bugherdSyncing ? 'Syncing' : 'Sync board'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </ChipsGroup>
                </TitleContent>
                {(project.completedDate === null || canEditCompleted) && canEditOverview && !isLocked && (
                    <>
                        {!isEditing ? (
                            <IconButton onClick={handleEditToggle}>
                                <Edit />
                            </IconButton>
                        ) : (
                            <Box>
                                {isUploading ? <CircularProgress size={20} /> : 
                                    <>
                                        <IconButton onClick={handleSave} disabled={tagDraft.loading || tagsSaving}>
                                            <Check />
                                        </IconButton>
                                        <IconButton onClick={handleEditToggle} disabled={tagsSaving}>
                                            <Close />
                                        </IconButton>
                                    </>
                                }
                            </Box>
                        )}
                    </>
                )}
            </TitleContainer>

            {!isEditing && bugherdSyncStats && (
                <SyncSummary variant="body2" incomplete={syncIsIncomplete(bugherdSyncStats)}>{syncSummaryText(bugherdSyncStats)}</SyncSummary>
            )}

            {isEditing ? (
                <>
                    <MarkdownEditor
                        initialContent={editedOverview}
                        onChange={handleOverviewChange}
                    />
                    <EditingContainer>
                        {canAssignProject && (
                            <FormControl fullWidth>
                                <InputLabel>Skill</InputLabel>
                                <Select value={skillId || ''} onChange={(e) => setSkillId(e.target.value)} label="Skill">
                                    {selectableSkills(skills, skillId).map((skill) => (
                                        <MenuItem key={skill._id} value={skill._id}>
                                            {skillOptionLabel(skill)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {canAssignProject && (
                            <FormControl fullWidth>
                                <InputLabel>Department</InputLabel>
                                <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} label="Department">
                                    {departments.map((department) => (
                                        <MenuItem key={department?._id} value={department?._id}>
                                            {department?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {(canAssignProject || canSetGroup) && (
                            <FormControl fullWidth>
                                <InputLabel>Group</InputLabel>
                                <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} label="Group">
                                    <MenuItem value="">Not in a group</MenuItem>
                                    {groups.map((group) => (
                                        <MenuItem key={group._id} value={group._id}>
                                            {group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {canToggleCommenting && (
                            <FormControlLabel
                                control={<Switch checked={editedCommentsEnabled} onChange={(e) => setEditedCommentsEnabled(e.target.checked)} />}
                                label="Enable Comments"
                            />
                        )}
                    </EditingContainer>
                    {canEditExternalBoard && (
                        <Box>
                            <ExternalBoardLabel variant="subtitle2">External QA board</ExternalBoardLabel>
                            <EditingContainer>
                                <FormControl fullWidth>
                                    <InputLabel>Provider</InputLabel>
                                    <Select value={editedBoardProvider} onChange={(e) => setEditedBoardProvider(e.target.value)} label="Provider">
                                        <MenuItem value="">No external board</MenuItem>
                                        <MenuItem value="bugherd">BugHerd</MenuItem>
                                        <MenuItem value="other">Another board</MenuItem>
                                    </Select>
                                </FormControl>
                                {editedBoardProvider && (
                                    <TextField
                                        fullWidth
                                        label="Board link"
                                        placeholder="https://"
                                        value={editedBoardUrl}
                                        onChange={(e) => setEditedBoardUrl(e.target.value)}
                                        disabled={boardUrlFromPicker}
                                        helperText={
                                            editedBoardProvider !== 'bugherd'
                                                ? "Paste the link to the team's board. The QA board icon on a task opens it in a new tab."
                                                : boardUrlFromPicker
                                                    ? 'Filled in from the board picked below.'
                                                    : 'Paste the board link from bugherd.com. The QA board icon on a task opens it in a new tab.'
                                        }
                                    />
                                )}
                                {showBugherdIdField && (
                                    <TextField
                                        fullWidth
                                        label="BugHerd board id"
                                        value={String(editedBugherdProjectId || '')}
                                        onChange={(e) => setEditedBugherdProjectId(e.target.value.replace(/[^0-9]/g, ''))}
                                        helperText="The number in the board's bugherd.com link."
                                    />
                                )}
                                {editedBoardProvider === 'bugherd' && (
                                    bugherdLoading ? (
                                        <BoardActionRow>
                                            <CircularProgress size={18} />
                                            <BoardHint variant="body2">Checking the BugHerd connection</BoardHint>
                                        </BoardActionRow>
                                    ) : bugherdError ? (
                                        <BoardActionRow>
                                            <Button variant="outlined" size="small" onClick={loadBugherd}>Try again</Button>
                                            <BoardHint variant="body2">BugHerd could not be reached just now.</BoardHint>
                                        </BoardActionRow>
                                    ) : bugherdPickerReady ? (
                                        <>
                                            <FormControl fullWidth>
                                                <InputLabel>BugHerd board</InputLabel>
                                                <Select
                                                    value={String(editedBugherdProjectId || '')}
                                                    onChange={(e) => handleBoardSelect(e.target.value)}
                                                    label="BugHerd board"
                                                >
                                                    {bugherdBoards.map((board) => (
                                                        <MenuItem key={board.id} value={String(board.id)}>
                                                            {board.name} ({board.totalTasks} tasks, archived included)
                                                        </MenuItem>
                                                    ))}
                                                    {editedBugherdProjectId && !selectedBugherdBoard && (
                                                        <MenuItem value={String(editedBugherdProjectId)}>
                                                            Board {String(editedBugherdProjectId)} (not in this BugHerd account)
                                                        </MenuItem>
                                                    )}
                                                </Select>
                                            </FormControl>
                                            {bugherdContext?.canConnect && (
                                                <BoardActionRow>
                                                    <Button size="small" color="error" onClick={() => setConfirmingDisconnect(true)}>
                                                        Disconnect BugHerd
                                                    </Button>
                                                    <BoardHint variant="body2">
                                                        {`Removes this client\u2019s BugHerd sign-in from Pollux${bugherdContext.connectedByEmail ? `, connected by ${bugherdContext.connectedByEmail}` : ''}.`}
                                                    </BoardHint>
                                                </BoardActionRow>
                                            )}
                                        </>
                                    ) : bugherdContext ? (
                                        <BoardActionRow>
                                            {!bugherdContext.clientId ? (
                                                <BoardHint variant="body2">
                                                    This project&apos;s account has no client, so there is nothing to connect BugHerd to.
                                                </BoardHint>
                                            ) : bugherdContext.canConnect ? (
                                                <>
                                                    <Button variant="outlined" size="small" onClick={handleConnectBugherd}>
                                                        {bugherdContext.needsReconnect ? 'Reconnect BugHerd' : 'Connect BugHerd'}
                                                    </Button>
                                                    <BoardHint variant="body2">
                                                        {bugherdContext.needsReconnect
                                                            ? 'The BugHerd connection stopped working and needs signing in again.'
                                                            : 'Sign in to BugHerd once for this client to choose a board.'}
                                                    </BoardHint>
                                                </>
                                            ) : (
                                                <BoardHint variant="body2">
                                                    BugHerd is not connected for this client. Someone who can edit clients needs to connect it.
                                                </BoardHint>
                                            )}
                                        </BoardActionRow>
                                    ) : null
                                )}
                            </EditingContainer>
                        </Box>
                    )}
                </>
            ) : (
                <>
                    {currentOverview ? (
                        <MarkdownDisplay content={currentOverview} />
                    ) : (
                        <Box>
                            <Skeleton variant="text" width="100%" height={20} />
                            <Skeleton variant="text" width="100%" height={20} />
                            <Skeleton variant="text" width="80%" height={20} />
                        </Box>
                    )}
                </>
            )}

            {tagsEditable && !groupChanged ? (
                <TagsRow>
                    <TagPicker
                        draft={tagDraft}
                        targetType="Project"
                        targetId={project._id}
                        clientId={project.account?.client?._id || project.account?.client || null}
                        clientName={project.account?.client?.name}
                        accountId={project.account}
                        sourceNames={tagSources}
                    />
                </TagsRow>
            ) : (
                (isEditing || projectTags.length > 0) && (
                    <TagsRow>
                        <TagPicker
                            value={projectTags}
                            targetType="Project"
                            targetId={project._id}
                            sourceNames={tagSources}
                            readOnly
                            lockedReason={isEditing ? (tagsEditable ? 'Save the group change before editing tags' : 'Completed work can’t be retagged') : undefined}
                        />
                    </TagsRow>
                )
            )}

            {!isEditing ? (
                <>
                    {project.attachments !== undefined && project.attachments.length > 0 && <Title>Attachments</Title>}
                    {project.attachments !== undefined &&
                        project.attachments.map((attachment) => (
                            <AttachmentBox
                                key={attachment._id}
                                attachment={attachment}
                                onReupload={(newAttachment) => dispatch(updateProjectState({ project: { attachments: [...project.attachments, newAttachment] } }))}
                            />
                        ))}
                </>
            ) : (
                <>{(project.completedDate === null || canEditCompleted) && <FilePondUploader setAttachments={setEditedAttachments} files={currentAttachments} onUploadStatusChange={setIsUploading} />}</>
            )}

            <ConfirmDialog
                open={confirmingDisconnect}
                onClose={() => setConfirmingDisconnect(false)}
                onConfirm={handleDisconnectBugherd}
                title="Disconnect BugHerd for this client?"
                message={`Every project for ${clientName || 'this client'} loses its BugHerd sign-in and stops syncing, not just this one.`}
                confirmLabel="Disconnect BugHerd"
                confirmColor="error"
            />
        </OverviewBox>
    );
};

export default ProjectOverview;